import React, { ChangeEvent, KeyboardEvent, ReactNode, memo, useCallback } from "react";
import "./textbox.scss";
import { FormInputElement, InputType } from "@archetypes/form-element";
import { LabelInfo } from "@components/label-info";
import { FormError } from "@components/form-error";
import { InfoIcon } from "@components/icons";
import { CustomTooltip } from "@components/tool-tip";

export interface TextboxProps extends FormInputElement {
  onChange?: (value: string) => void;
  value?: string | number;
  type?: InputType;
  containerClassName?: string;
  fieldIcon?: ReactNode;
  errorMessage?: string;
  maxLength?: number;
  labelClassName?: string;
  infoText?: string;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  onKeyPress?: (e: KeyboardEvent<HTMLInputElement>) => void;
  wrapperClass?: string;
  max?: string | number;
  min?: string | number;
  formMode?: string;
  questionId?: string;
  disabled?: boolean;
  IsOTPValidationError?: boolean;
  flexDirection?: string;
  variant?: "button";
  onClick?: () => void;
  notification?: number;
  tooltipMessage?: string;
  className?: string;
  style?: React.CSSProperties;
  ref?: React.Ref<HTMLInputElement>;
}

const allowEventForInputTypeNumber = (e: KeyboardEvent<HTMLInputElement>, type: string) => {
  const allowedKeysForNumberInput = [
    "0",
    "1",
    "2",
    "3",
    "4",
    "5",
    "6",
    "7",
    "8",
    "9",
    "Backspace",
    "Delete",
    "-",
    "+",
    ".",
    "ArrowLeft",
    "ArrowRight",
    "Tab",
    "Enter",
  ];
  if (
    type === "number" &&
    !e.altKey &&
    !e.ctrlKey &&
    !e.shiftKey &&
    !e.metaKey &&
    !allowedKeysForNumberInput.includes(e.key)
  ) {
    return false;
  }
  return true;
};

const TextboxComponent = ({
  onChange,
  placeholder = "Type Something...",
  label,
  type = "text",
  containerClassName,
  fieldIcon,
  children,
  errorMessage,
  required,
  labelClassName,
  infoText,
  wrapperClass,
  max,
  min,
  value,
  flexDirection,
  variant,
  onClick,
  notification,
  tooltipMessage,
  className,
  style,
  id,
  ref,
  ...restProps
}: TextboxProps) => {
  const handleTextChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;
      onChange?.(inputValue);
    },
    [onChange],
  );

  return (
    <div className={`${containerClassName ?? ""} w-100`}>
      <LabelInfo
        label={label}
        labelClassName={labelClassName}
        required={required}
        infoText={infoText}
        htmlFor={id}
      />
      <div
        className={`textbox-wrapper ${!fieldIcon ? "flex" : ""} ${errorMessage ? "error" : ""} ${
          flexDirection === "column" ? "flex-col" : ""
        } ${wrapperClass}
          }`}
      >
        {fieldIcon && <span className="field-icon">{fieldIcon}</span>}
        {variant === "button" ? (
          <button
            type="button"
            onClick={onClick}
            className={`textbox${className ? ` ${className}` : ""}`}
            style={style}
            disabled={restProps.disabled}
          >
            {placeholder}
            {notification
              ? notification > 0 && <span className="notification">{notification}</span>
              : ""}
          </button>
        ) : (
          <input
            type={type}
            tabIndex={0}
            onChange={handleTextChange}
            required={required}
            placeholder={placeholder}
            onWheel={(e) => {
              type === "number" && e.currentTarget.blur();
            }}
            onKeyPress={(e) => {
              if (!allowEventForInputTypeNumber(e, type)) {
                e.preventDefault();
                return;
              }
              // Enforce maxLength check as its failing otherwise for Android devices
              if (restProps.maxLength) {
                return e.currentTarget.value.length > restProps.maxLength ? false : true;
              } else {
                restProps.onKeyPress?.(e);
              }
            }}
            max={max}
            min={min}
            value={value ?? ""}
            id={id}
            {...restProps}
            ref={ref}
          />
        )}
        {tooltipMessage && (
          <CustomTooltip
            content={tooltipMessage}
            maxWidth={240}
            className="text-wrap"
            wrapperClass="align-center"
          >
            <InfoIcon color="#999999" size="10" />{" "}
          </CustomTooltip>
        )}
        {children}
      </div>
      <FormError errorMessage={errorMessage} />
    </div>
  );
};

TextboxComponent.displayName = "TextboxComponent";

export const Textbox = memo(TextboxComponent);
